/**
 * AdvertisePage - public landing page for the BuildPilot Featured
 * Business advertising program. Content derived from the team's
 * Featured Business Pack PDF: target audience, where placements
 * appear, key reach stats, compliance disclaimer, and a single
 * primary CTA that opens the AdvertisingEnquiryModal.
 *
 * Compliance: explicitly states "Featured business placements do not
 * constitute endorsement or recommendation by BuildPilot" - mirrors
 * the disclaimer copy in the pack.
 */
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import RelatedLinks from '../components/related/RelatedLinks';
import AdvertisingEnquiryModal from '../components/ads/AdvertisingEnquiryModal';

const STATS = [
  { v: '72%', l: 'South Australian audience' },
  { v: '65,000+', l: 'Members across building & renovation communities' },
  { v: '30,000+', l: 'Targeted email database, built over 5 years' },
  { v: '50,000+', l: 'Monthly views across building content' },
  { v: '3.56%', l: 'Average in-app click-through rate' },
];

const PLACEMENTS = [
  {
    title: 'Suburb guide spotlights',
    body: 'Featured on the 68+ Adelaide suburb pages your future customers are already researching.',
  },
  {
    title: 'Read Guides articles',
    body: 'Native, topic-relevant placements inside our editorial library covering cost, design, contracts and more.',
  },
  {
    title: 'Build-stage CoPilot screens',
    body: 'Appears to free users at the stage that matches your service - frame stage, lock-up, handover, etc.',
  },
  {
    title: 'Design search hub',
    body: 'Featured plans row inside the Search Designs and Explore Home & Land surfaces.',
  },
  {
    title: 'Builder, trade & supplier profile pages',
    body: 'Native unit at the foot of each public profile page (never inside the shortlist results).',
  },
  {
    title: 'Building Types overview pages',
    body: 'Topic-matched units across KDR, first-home, renovation, dual-occ and townhouse hubs.',
  },
];

const REASONS = [
  {
    title: 'Targeted building audience',
    body: 'Australians actively planning new homes, renovations and home improvement projects.',
  },
  {
    title: 'Positioned throughout the journey',
    body: 'Repeated, contextual exposure at the stages buyers actually make decisions.',
  },
  {
    title: 'Visual, design-driven platform',
    body: 'Your brand sits inside a modern platform focused on inspiration, planning and confidence.',
  },
  {
    title: 'Performance & engagement insights',
    body: 'Monthly featured business reports with click tracking, visibility insights and active user data.',
  },
];

const ARTWORK_SPECS = [
  { name: 'Banner (small)', size: '800 × 150 px' },
  { name: 'Banner (medium)', size: '800 × 250 px' },
  { name: 'Square', size: '1080 × 1080 px' },
  { name: 'Full-bleed mobile', size: '1284 × 2778 px' },
];

// Plain-English benefits — written for business owners with little or no
// marketing background. No jargon (no "CTR", "impressions", "intent", etc).
const PLAIN_BENEFITS = [
  {
    id: 'right-pages',
    title: 'You show up where your customers are already looking',
    body: 'Your ad appears on the exact pages people read while planning the kind of work you do — so you reach them at the moment they are deciding, not at random.',
  },
  {
    id: 'your-state',
    title: 'We only show your ad to people in your state',
    body: 'You won\u2019t waste a cent reaching someone on the other side of the country you could never take on as a job.',
  },
  {
    id: 'people-tap',
    title: 'People here actually tap the ads',
    body: 'Nearly 4 in every 100 people who see an ad here click it. Most ads online get fewer than 1 in 100 — ours work because they match what the person is already reading.',
  },
  {
    id: 'keeps-working',
    title: 'Your ad keeps working month after month',
    body: 'It sits on pages that keep showing up on Google for years — not just for a week like a boosted social post.',
  },
  {
    id: 'simple-report',
    title: 'You get a simple report every month',
    body: 'We tell you plainly how many people saw your ad and how many tapped it, so you never have to guess whether it worked.',
  },
  {
    id: 'trusted-spot',
    title: 'People trust what they see next to your ad',
    body: 'Paying us never bumps you up our builder recommendations, and we never hide that an ad is an ad. That honesty is exactly why buyers trust the businesses they find here.',
  },
];

const AdvertisePage = () => {
  const [showEnquiry, setShowEnquiry] = useState(false);

  return (
    <>
      <Helmet>
        <title>Advertise with BuildPilot - Featured Business Program</title>
        <meta name="description" content="Reach Australians actively building, renovating and improving. Featured business placements across BuildPilot's suburb guides, read guides, profile pages and CoPilot stage screens." />
        <link rel="canonical" href="https://buildpilot.com.au/advertise" />
      </Helmet>

      <Header />

      <main style={{ background: '#ffffff' }}>
        {/* ───────────── HERO ───────────── */}
        <section style={{
          position: 'relative',
          background: '#0a2540',
          color: '#ffffff',
          padding: 'clamp(160px, 22vh, 220px) 24px 64px',
          backgroundImage: 'linear-gradient(rgba(10,37,64,0.72), rgba(10,37,64,0.88)), url(/images/brand-mockups/billboard-sunset.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
            <span style={{
              display: 'inline-block', padding: '6px 14px', background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999, fontSize: 12,
              fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 24,
            }}>
              Featured Business Program
            </span>
            <h1 style={{
              fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 700, lineHeight: 1.1,
              letterSpacing: '-0.02em', margin: '0 0 20px', maxWidth: 880,
            }}>
              Show your brand to Australians actively building, renovating &amp; improving.
            </h1>
            <p style={{
              fontSize: 'clamp(16px, 2vw, 19px)', lineHeight: 1.5, color: 'rgba(255,255,255,0.75)',
              maxWidth: 760, margin: '0 0 36px',
            }}>
              BuildPilot helps Australians navigate the building journey through technology, tools, education and featured businesses. Position your brand inside the platform they use to make decisions.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setShowEnquiry(true)}
                data-testid="advertise-hero-cta"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 22px', background: '#ffffff', color: '#0a2540',
                  border: 0, borderRadius: 12, fontSize: 15, fontWeight: 600,
                  cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                }}
              >
                Enquire about advertising →
              </button>
            </div>
          </div>
        </section>

        {/* ───────────── STATS BAR ───────────── */}
        <section style={{ padding: '48px 24px', background: '#f5f7fa', borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            {STATS.map((s) => (
              <div key={s.l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 30, fontWeight: 700, color: '#0a2540', letterSpacing: '-0.02em', lineHeight: 1.1 }}>{s.v}</div>
                <div style={{ fontSize: 13, color: '#374151', marginTop: 6, lineHeight: 1.4 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ───────────── PLAIN-ENGLISH BENEFITS ───────────── */}
        <section data-testid="advertise-plain-benefits" style={{ padding: '72px 24px', background: '#eef5ff', borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 700, color: '#0a2540', letterSpacing: '-0.02em', margin: '0 0 12px' }}>
              What this actually means for your business
            </h2>
            <p style={{ fontSize: 16, color: '#4b5563', lineHeight: 1.6, maxWidth: 740, margin: '0 0 40px' }}>
              No marketing jargon — here&apos;s the plain version of why a placement on BuildPilot works.
            </p>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              {PLAIN_BENEFITS.map((b) => (
                <div
                  key={b.id}
                  data-testid={`plain-benefit-${b.id}`}
                  style={{ display: 'flex', gap: 14, padding: 20, background: '#ffffff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 12 }}
                >
                  <span style={{ display: 'inline-flex', width: 28, height: 28, borderRadius: 999, background: 'rgba(29,142,254,0.12)', color: '#1d8efe', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  </span>
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0a2540', margin: '0 0 6px', lineHeight: 1.35 }}>{b.title}</h3>
                    <p style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.55, margin: 0 }}>{b.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ───────────── WHY FEATURE ───────────── */}
        <section style={{ padding: '72px 24px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 700, color: '#0a2540', letterSpacing: '-0.02em', margin: '0 0 12px' }}>
              Why feature on BuildPilot
            </h2>
            <p style={{ fontSize: 16, color: '#4b5563', lineHeight: 1.6, maxWidth: 740, margin: '0 0 40px' }}>
              Users engage with BuildPilot throughout multiple stages of their building and renovation journey. Your business gets seen when intent is highest.
            </p>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
              {REASONS.map((r) => (
                <div key={r.title} style={{ padding: 20, background: '#ffffff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 12 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: '#0a2540', margin: '0 0 6px' }}>{r.title}</h3>
                  <p style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.5, margin: 0 }}>{r.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ───────────── WHERE YOU'LL APPEAR ───────────── */}
        <section style={{ padding: '64px 24px', background: '#f5f7fa' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 700, color: '#0a2540', letterSpacing: '-0.02em', margin: '0 0 12px' }}>
              Where your business appears
            </h2>
            <p style={{ fontSize: 16, color: '#4b5563', lineHeight: 1.6, maxWidth: 740, margin: '0 0 40px' }}>
              Businesses are positioned across the BuildPilot ecosystem to maximise visibility during the stages that matter. We never place ads inside Mini Matchers, Shortlists or the homepage - placements only appear in content surfaces.
            </p>
            <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              {PLACEMENTS.map((p) => (
                <div key={p.title} style={{ padding: '20px 22px', background: '#ffffff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 12 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: '#0a2540', margin: '0 0 6px' }}>{p.title}</h3>
                  <p style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.5, margin: 0 }}>{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ───────────── ARTWORK + INSIGHTS ───────────── */}
        <section style={{ padding: '64px 24px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gap: 32, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0a2540', margin: '0 0 12px', letterSpacing: '-0.01em' }}>Artwork specifications</h2>
              <p style={{ fontSize: 14, color: '#4b5563', margin: '0 0 16px', lineHeight: 1.55 }}>
                Provide finished artwork in the sizes below. Our team can assist with artwork setup if required.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {ARTWORK_SPECS.map((a) => (
                  <li key={a.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 13.5 }}>
                    <span style={{ color: '#374151', fontWeight: 500 }}>{a.name}</span>
                    <span style={{ color: '#6b7280', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{a.size}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0a2540', margin: '0 0 12px', letterSpacing: '-0.01em' }}>Monthly performance insights</h2>
              <p style={{ fontSize: 14, color: '#4b5563', margin: '0 0 12px', lineHeight: 1.55 }}>
                You&apos;ll receive a Featured Business report covering:
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
                {['Click tracking', 'Visibility insights', 'Engagement analytics', 'Active user interaction data'].map((item) => (
                  <li key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: '#374151' }}>
                    <span style={{ display: 'inline-flex', width: 20, height: 20, borderRadius: 999, background: 'rgba(29,142,254,0.12)', color: '#1d8efe', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ───────────── CTA BANNER ───────────── */}
        <section style={{ padding: '64px 24px', background: '#0a2540', color: '#ffffff' }}>
          <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
            <span style={{
              display: 'inline-block', padding: '6px 14px', background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999, fontSize: 12,
              fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 20,
            }}>
              Founding Featured Business
            </span>
            <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 34px)', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
              Limited placements available across selected categories.
            </h2>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, margin: '0 0 28px' }}>
              Secure early positioning inside the BuildPilot ecosystem as the platform continues to grow nationally.
            </p>
            <button
              type="button"
              onClick={() => setShowEnquiry(true)}
              data-testid="advertise-footer-cta"
              style={{
                padding: '14px 26px', background: '#ffffff', color: '#0a2540',
                border: 0, borderRadius: 12, fontSize: 15, fontWeight: 600,
                cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
              }}
            >
              Enquire about advertising →
            </button>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)', marginTop: 28, lineHeight: 1.5 }}>
              Featured business placements do not constitute endorsement or recommendation by BuildPilot.
              Ads never influence organic match rankings.
            </p>
          </div>
        </section>

        {/* ───────────── CONTACT ───────────── */}
        <section style={{ padding: '48px 24px', background: '#ffffff', borderTop: '1px solid rgba(15,23,42,0.06)' }}>
          <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', fontSize: 14, color: '#4b5563', lineHeight: 1.6 }}>
            <p style={{ margin: '0 0 12px', fontWeight: 600, color: '#0a2540' }}>Get in touch</p>
            <button
              onClick={() => setShowEnquiry(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 22px', background: '#1d8efe', color: '#ffffff',
                border: 0, borderRadius: 999, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', boxShadow: '0 6px 18px rgba(29,142,254,0.35)',
              }}
            >
              Enquire about advertising →
            </button>
          </div>
        </section>
      </main>

      <RelatedLinks theme="business" heading="More about BuildPilot" />
      <Footer />

      {showEnquiry && (
        <AdvertisingEnquiryModal
          sourcePage="advertise-landing"
          onClose={() => setShowEnquiry(false)}
        />
      )}
    </>
  );
};

export default AdvertisePage;
